import { isAxiosError } from 'axios';

export const parsePaginationResponse = ({ data: { count, next, previous, results, ...rest } }) => ({
  count,
  next,
  previous,
  results,
  ...rest,
});

const FALLBACK_ERROR_MESSAGE = 'Erro ao processar requisição';
const HTML_ERROR_PATTERN = /<!doctype\s+html|<html[\s>]|<body[\s>]|<script[\s>]/i;

const getSafeErrorMessage = (message, fallback = FALLBACK_ERROR_MESSAGE) => {
  if (typeof message !== 'string') return fallback;

  const normalizedMessage = message.trim();

  if (!normalizedMessage || HTML_ERROR_PATTERN.test(normalizedMessage)) return fallback;

  return normalizedMessage;
};

const toErrorMessage = (value, fallback = FALLBACK_ERROR_MESSAGE) => {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
};

const flattenErrorMessages = (value, path = '') => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => {
      const itemPath = path ? `${path}.${index}` : String(index);
      return flattenErrorMessages(item, item !== null && typeof item === 'object' ? itemPath : path);
    });
  }

  if (value !== null && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) =>
      flattenErrorMessages(item, path ? `${path}.${key}` : key)
    );
  }

  const message = toErrorMessage(value, '');
  return message ? [`${path ? `${path}: ` : ''}${message}`] : [];
};

const normalizeFieldErrors = (fieldErrors) =>
  (Array.isArray(fieldErrors) ? fieldErrors : [])
    .map(({ field, errors }) => ({
      field: toErrorMessage(field, ''),
      errors: flattenErrorMessages(errors),
    }))
    .filter(({ field, errors }) => field && errors.length);

const translateApiMessage = (message) => {
  if (typeof message !== 'string') return message;

  return message.replace(
    /^Pedido foi limitado\. Expected available in (\d+) seconds\.$/,
    'Pedido foi limitado. Estará disponível novamente em $1 segundos.'
  );
};

export function AppError(error) {
  const { message, fieldErrors, status } = parseResponseError(error);
  this.isAppError = true;
  this.message = translateApiMessage(getSafeErrorMessage(message));
  this.fieldErrors = fieldErrors;
  this.status = status;
  // Identificador de regra que o backend manda ao lado do `detail`, pra tela reagir sem comparar
  // texto. Como array é erro do campo `code` (cupom) e já está em `fieldErrors`.
  const code = error?.response?.data?.code;
  this.code = typeof code === 'string' ? code : undefined;
}

AppError.prototype = Object.create(Error.prototype);
AppError.prototype.constructor = AppError;

/**
 * Parses API error responses into a standardized format
 * @param {Object} error - The error object from axios/API response
 * @param {Object} [error.response] - The response object containing status and data
 * @param {number} [error.response.status] - HTTP status code
 * @param {Object} [error.response.data] - Response data containing error information
 * @param {string} [defaultMessage='Erro ao processar requisição'] - Default error message if none is provided
 * @returns {Object} Standardized error object
 * @returns {string} return.message - General error message
 * @returns {Array<{field: string, errors: string[]}>} return.fieldErrors - Array of field-specific errors
 * @example
 * // Field validation errors
 * // Input:
 * // {
 * //   response: {
 * //     status: 400,
 * //     data: {
 * //       code: ["Cupom com este código já existe."],
 * //       anotherField: ["Another error"]
 * //     }
 * //   }
 * // }
 * // Output:
 * // {
 * //   message: "Erro ao processar requisição",
 * //   fieldErrors: [
 * //     { field: "code", errors: ["Cupom com este código já existe."] },
 * //     { field: "anotherField", errors: ["Another error"] }
 * //   ]
 * // }
 */
export const parseResponseError = (error) => {
  if (!isAxiosError(error)) {
    return {
      message: getSafeErrorMessage(toErrorMessage(error?.message)),
      fieldErrors: normalizeFieldErrors(error?.fieldErrors),
      status: error?.status,
    };
  }

  const { status, data } = error.response ?? {
    status: 500,
  };

  const messages = {
    400: 'Erro ao processar requisição',
    401: 'Você não tem permissão para acessar este recurso',
    403: 'Você não tem permissão para acessar este recurso',
    404: 'Recurso não encontrado',
    424: 'Serviço de validação temporariamente indisponível. Tente novamente em alguns instantes.',
    500: 'Erro interno do servidor',
  };

  if (!status) {
    return {
      message: 'Erro de conexão, tente novamente em alguns minutos.',
      fieldErrors: [],
      status,
    };
  }

  if (status >= 400 && status < 500 && Array.isArray(data)) {
    const [message] = data || [messages[status] || FALLBACK_ERROR_MESSAGE];
    return {
      message: getSafeErrorMessage(
        toErrorMessage(message, messages[status] || FALLBACK_ERROR_MESSAGE),
        messages[status] || FALLBACK_ERROR_MESSAGE
      ),
      fieldErrors: [],
      status,
    };
  }

  if (status >= 400 && status < 500 && data !== null && typeof data === 'object') {
    const { message, detail, ...rest } = data;

    const fieldErrors = normalizeFieldErrors(
      Object.entries(rest).map(([field, errors]) => ({ field, errors }))
    );

    return {
      message: getSafeErrorMessage(
        toErrorMessage(message ?? detail, messages[status] || FALLBACK_ERROR_MESSAGE),
        messages[status] || FALLBACK_ERROR_MESSAGE
      ),
      fieldErrors,
      status,
    };
  }

  if (status >= 500 && status < 600) {
    return {
      message: messages[500],
      fieldErrors: [],
      status,
    };
  }

  return {
    message: messages[status] || FALLBACK_ERROR_MESSAGE,
    fieldErrors: [],
    status,
  };
};

export const isRequestCanceledError = (error) => isAxiosError(error) && error.code === 'ERR_CANCELED';

export const getFirstErrorMessage = (error, fallback) =>
  error?.fieldErrors?.[0]?.errors?.[0] || error?.message || fallback;

export const getFieldErrorMessages = (error, field) =>
  error?.fieldErrors?.find((fieldError) => fieldError.field === field)?.errors ?? [];
